import { useState, useEffect } from 'react';
import { revenueAPI, payrollAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  TrendingUp,
  IndianRupee,
  Stethoscope,
  Users,
  Receipt,
  PieChart,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const RevenuePayoutsPage = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [revenue, setRevenue] = useState(null);
  const [doctorPayouts, setDoctorPayouts] = useState([]);
  const [staffPayroll, setStaffPayroll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [month, year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [revRes, docRes, staffRes] = await Promise.all([
        revenueAPI.summary({ month, year }),
        payrollAPI.doctorPayouts({ month, year }),
        payrollAPI.staffPayroll({ month, year }),
      ]);
      setRevenue(revRes.data);
      setDoctorPayouts(docRes.data || []);
      setStaffPayroll(staffRes.data || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (delta) => {
    let m = month + delta, y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setMonth(m);
    setYear(y);
  };

  const totalDoctorPayout = doctorPayouts.reduce((s, d) => s + d.doctor_payout, 0);
  const totalStaffSalary = staffPayroll.reduce((s, p) => s + p.net_salary, 0);

  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><div className="grid grid-cols-4 gap-4">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}</div></div>;

  return (
    <div className="space-y-6" data-testid="revenue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Revenue & Payouts</h1>
          <p className="mt-1 text-slate-600">Financial overview, doctor payouts, and staff payroll</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => handleMonthChange(-1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="rev-prev-month"><ChevronLeft className="h-5 w-5" /></button>
          <span className="font-semibold text-slate-900 min-w-[140px] text-center">{monthNames[month - 1]} {year}</span>
          <button onClick={() => handleMonthChange(1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="rev-next-month"><ChevronRight className="h-5 w-5" /></button>
        </div>
      </div>

      {/* Revenue Summary Cards */}
      {revenue && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Billed</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">Rs. {revenue.total_billed.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50"><Receipt className="h-6 w-6 text-blue-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Collected</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">Rs. {revenue.total_collected.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-50"><IndianRupee className="h-6 w-6 text-emerald-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1">Rs. {revenue.total_pending.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50"><TrendingUp className="h-6 w-6 text-amber-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Invoices</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{revenue.total_invoices}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-100"><PieChart className="h-6 w-6 text-slate-600" /></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue Breakdown */}
      {revenue?.revenue_breakdown && (
        <Card>
          <CardHeader><CardTitle className="text-base">Revenue Classification</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'OPD', value: revenue.revenue_breakdown.opd, color: 'emerald' },
                { label: 'Procedure', value: revenue.revenue_breakdown.procedure, color: 'blue' },
                { label: 'IPD', value: revenue.revenue_breakdown.ipd, color: 'purple' },
                { label: 'Misc', value: revenue.revenue_breakdown.misc, color: 'slate' },
              ].map(r => (
                <div key={r.label} className={`p-4 bg-${r.color}-50 rounded-lg`}>
                  <p className={`text-sm text-${r.color}-600`}>{r.label}</p>
                  <p className={`text-xl font-bold text-${r.color}-700 mt-1`}>Rs. {r.value.toLocaleString()}</p>
                </div>
              ))}
            </div>
            {(revenue.total_gst > 0 || revenue.total_discount > 0) && (
              <div className="flex gap-6 mt-4 text-sm text-slate-500">
                <span>GST Collected: <span className="font-medium text-slate-900">Rs. {revenue.total_gst.toLocaleString()}</span></span>
                <span>Discounts Given: <span className="font-medium text-slate-900">Rs. {revenue.total_discount.toLocaleString()}</span></span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="doctors" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="doctors" data-testid="tab-doctor-payouts"><Stethoscope className="h-4 w-4 mr-2" /> Doctor Payouts ({doctorPayouts.length})</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff-payroll"><Users className="h-4 w-4 mr-2" /> Staff Payroll ({staffPayroll.length})</TabsTrigger>
        </TabsList>

        {/* Doctor Payouts */}
        <TabsContent value="doctors">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Doctor Payouts</CardTitle>
                <Badge className="bg-emerald-100 text-emerald-700 text-sm">Total: Rs. {totalDoctorPayout.toLocaleString()}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {doctorPayouts.length === 0 ? (
                <div className="text-center py-8 text-slate-500"><Stethoscope className="h-10 w-10 mx-auto mb-3 text-slate-300" /><p>No doctor payouts for this period</p></div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Doctor</TableHead>
                        <TableHead>Specialization</TableHead>
                        <TableHead>Revenue Share</TableHead>
                        <TableHead>Total Revenue</TableHead>
                        <TableHead>OPD</TableHead>
                        <TableHead>Procedure</TableHead>
                        <TableHead>Doctor Payout</TableHead>
                        <TableHead>Hospital Share</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {doctorPayouts.map(d => (
                        <TableRow key={d.doctor_id} data-testid={`doctor-payout-${d.doctor_id}`}>
                          <TableCell className="font-medium">{d.doctor_name}</TableCell>
                          <TableCell>{d.specialization || '-'}</TableCell>
                          <TableCell><Badge variant="outline">{d.revenue_share_percent}%</Badge></TableCell>
                          <TableCell>Rs. {d.total_revenue.toLocaleString()}</TableCell>
                          <TableCell className="text-emerald-600">Rs. {d.opd_revenue.toLocaleString()}</TableCell>
                          <TableCell className="text-blue-600">Rs. {d.procedure_revenue.toLocaleString()}</TableCell>
                          <TableCell className="font-semibold text-emerald-700">Rs. {d.doctor_payout.toLocaleString()}</TableCell>
                          <TableCell className="text-slate-600">Rs. {d.hospital_share.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Payroll */}
        <TabsContent value="staff">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Staff Payroll</CardTitle>
                <Badge className="bg-blue-100 text-blue-700 text-sm">Total: Rs. {totalStaffSalary.toLocaleString()}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {staffPayroll.length === 0 ? (
                <div className="text-center py-8 text-slate-500"><Users className="h-10 w-10 mx-auto mb-3 text-slate-300" /><p>No staff payroll data</p></div>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Employee</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Salary</TableHead>
                        <TableHead>Working Days</TableHead>
                        <TableHead>Present</TableHead>
                        <TableHead>Absent</TableHead>
                        <TableHead>LWP Days</TableHead>
                        <TableHead>LWP Deduction</TableHead>
                        <TableHead>Net Salary</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {staffPayroll.map(p => (
                        <TableRow key={p.user_id} data-testid={`payroll-${p.user_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{p.name}</p>
                              <p className="text-xs text-slate-500">{p.employee_code} | {p.designation}</p>
                            </div>
                          </TableCell>
                          <TableCell>{p.department}</TableCell>
                          <TableCell>Rs. {p.monthly_salary.toLocaleString()}</TableCell>
                          <TableCell>{p.working_days}</TableCell>
                          <TableCell className="text-emerald-600">{p.present_days}</TableCell>
                          <TableCell className="text-red-600">{p.absent_days}</TableCell>
                          <TableCell>{p.lwp_days > 0 ? <Badge className="bg-red-100 text-red-700">{p.lwp_days}</Badge> : <span className="text-slate-400">0</span>}</TableCell>
                          <TableCell className="text-red-600">{p.lwp_deduction > 0 ? `- Rs. ${p.lwp_deduction.toLocaleString()}` : '-'}</TableCell>
                          <TableCell className="font-semibold text-slate-900">Rs. {p.net_salary.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RevenuePayoutsPage;
